import { createStore } from 'vuex'
import VuexPersistence from 'vuex-persist'
import auth from './modules/auth'
import ui from './modules/ui'
import marketplace from './modules/marketplace'
import userApps from './modules/userApps'
import billing from './modules/billing'
import backups from './modules/backups'
import domains from './modules/domains'
import devEnvironments from './modules/devEnvironments'
import api from '@/services/api'
import { dynamicVuexStorage, readToken, clearToken, ACCESS_TOKEN_KEY } from '@/utils/token-storage'

// Configure persistence for auth state. dynamicVuexStorage routes writes
// to localStorage when "Remember me" is checked and sessionStorage
// otherwise — so closing the tab forgets the user when they didn't ask
// to be remembered.
const vuexLocal = new VuexPersistence({
  key: 'vuex-persist',
  storage: dynamicVuexStorage,
  reducer: (state) => {
    return {
      auth: {
        accessToken: state.auth.accessToken,
        refreshToken: state.auth.refreshToken,
        tokenValidSeconds: state.auth.tokenValidSeconds,
        user: state.auth.user,
        isAuthenticated: state.auth.isAuthenticated
      }
    }
  }
})

export default createStore({
  state: {
    // Global state
    loading: false,
    notification: {
      show: false,
      type: 'info',
      message: '',
      timeout: 5000
    },
    showAuthModal: false,
    authModalType: 'login', // 'login', 'register', 'forgot-password'
    // Marketplace Phase 2 (design spec §1.2) — when an unauthenticated user
    // triggers an auth-gated flow (e.g. clicks Deploy), we stash the target
    // path here. The auth store's login-success path consumes it once and
    // router.pushes there, making anon → login → continue seamless.
    postAuthRedirect: null,
    // Phase G v1.2 — populated when an unauthenticated user arrives via
    // an invitation email link. Lets LoginModal/RegisterModal show a
    // contextual banner and prefill the email field. Cleared on close.
    invitationContext: null,
    securityLogs: []
  },

  getters: {
    isLoading: state => state.loading,
    hasNotification: state => state.notification.show,
    isAuthModalVisible: state => state.showAuthModal
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_NOTIFICATION(state, notification) {
      state.notification = {
        show: true,
        type: notification.type || 'info',
        message: notification.message || '',
        timeout: notification.timeout || 5000
      }
    },

    CLEAR_NOTIFICATION(state) {
      state.notification.show = false
    },

    setShowAuthModal(state, show) {
      state.showAuthModal = show
    },

    setAuthModalType(state, type) {
      state.authModalType = type
    },

    // Marketplace Phase 2 (design spec §1.2) — set/clear the one-shot
    // post-authentication redirect target. Pass null to clear after consuming.
    setPostAuthRedirect(state, path) {
      state.postAuthRedirect = path || null
    },

    setInvitationContext(state, context) {
      state.invitationContext = context || null
    },

    ADD_SECURITY_LOG(state, log) {
      state.securityLogs.push({
        ...log,
        id: Date.now(),
        timestamp: new Date().toISOString()
      })

      // Keep only last 100 logs
      if (state.securityLogs.length > 100) {
        state.securityLogs = state.securityLogs.slice(-100)
      }
    }
  },

  actions: {
    async showNotification({ commit }, notification) {
      commit('SET_NOTIFICATION', notification)

      // Auto-clear notification after timeout
      setTimeout(() => {
        commit('CLEAR_NOTIFICATION')
      }, notification.timeout || 5000)
    },

    clearNotification({ commit }) {
      commit('CLEAR_NOTIFICATION')
    },

    async checkAuthToken({ dispatch }) {
      const token = readToken(ACCESS_TOKEN_KEY)
      if (token) {
        try {
          const response = await api.verifyToken(token)
          if (response.data.valid) {
            dispatch('auth/setAuthData', {
              isAuthenticated: true,
              token,
              user: response.data.user
            })
          } else {
            clearToken(ACCESS_TOKEN_KEY)
          }
        } catch (error) {
          console.error('Token verification failed:', error)
          clearToken(ACCESS_TOKEN_KEY)
        }
      }
    },

    logSecurityEvent({ commit }, event) {
      commit('ADD_SECURITY_LOG', {
        ...event,
        type: 'security_event',
        userAgent: navigator.userAgent,
        url: window.location.href
      })

      // Send to monitoring service in production
      if (import.meta.env.PROD) {
        api.logSecurityEvent(event).catch(error => {
          console.error('Failed to send security log:', error)
        })
      }
    },

    logPageView({ commit }, pageData) {
      commit('ADD_SECURITY_LOG', {
        ...pageData,
        type: 'page_view',
        userAgent: navigator.userAgent
      })

      // Send to analytics service
      if (import.meta.env.PROD && window.gtag) {
        window.gtag('event', 'page_view', {
          page_path: pageData.path,
          page_title: document.title
        })
      }
    },

    logError({ commit }, error) {
      commit('ADD_SECURITY_LOG', {
        ...error,
        type: 'error',
        userAgent: navigator.userAgent,
        url: window.location.href
      })

      // Send to error tracking service in production
      if (import.meta.env.PROD) {
        // Add your error tracking service here (Sentry, etc.)
        console.error('Production error:', error)
      }
    }
  },

  modules: {
    auth,
    ui,
    marketplace,
    userApps,
    billing,
    backups,
    domains,
    // P8 — Dev Environments capability probe + list (NOT persisted; the
    // reducer above whitelists only `auth`).
    devEnvironments
  },

  // Add vuex-persist plugin
  plugins: [vuexLocal.plugin],

  // Enable strict mode in development
  strict: !import.meta.env.PROD
})
